import React, { useEffect, useState } from 'react';
import {
  ShoppingCart, Search, Package, Calendar, CheckCircle2, Clock, XCircle, Truck, FileText, Grid3x3, List as ListIcon, Table as TableIcon, ChevronUp, ChevronDown, X, Euro, Download
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { listMyOrders, type BuyerOrder, getMyProfile, type BuyerProfile } from '../../services/clientService';
import api, { API_BASE_URL } from '../../services/api';
import { useAuthStore } from '../../stores/authStore';
import { useAlert } from '../../components/AlertProvider';
import { useAuth } from '../../contexts/AuthContext';
import { ShieldAlert } from 'lucide-react';


const statusConfig = {
  pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-950/30 dark:text-yellow-300', icon: Clock },
  confirmed: { label: 'Confirmée', color: 'bg-blue-100 text-blue-700 dark:bg-blue-950/30 dark:text-blue-300', icon: CheckCircle2 },
  shipped: { label: 'Expédiée', color: 'bg-purple-100 text-purple-700 dark:bg-purple-950/30 dark:text-purple-300', icon: Truck },
  delivered: { label: 'Livrée', color: 'bg-green-100 text-green-700 dark:bg-green-950/30 dark:text-green-300', icon: CheckCircle2 },
  invoiced: { label: 'Facturée', color: 'bg-amber-100 text-amber-700 dark:bg-amber-950/30 dark:text-amber-300', icon: FileText },
  paid: { label: 'Payée', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300', icon: CheckCircle2 },
  cancelled: { label: 'Annulée', color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300', icon: XCircle },
} as const;

const BuyerOrdersTailwind: React.FC = () => {
  const navigate = useNavigate();
  useAlert();
  const { user } = useAuth();
  const [orders, setOrders] = useState<BuyerOrder[]>([]);
  const [profile, setProfile] = useState<BuyerProfile | null>(null);
  const [showBlockedModal, setShowBlockedModal] = useState<boolean>(false);
  const [busy, setBusy] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');
  const [viewMode, setViewMode] = useState<'card' | 'table' | 'list'>(() =>
    typeof window !== 'undefined' && window.innerWidth < 768 ? 'card' : 'table'
  );
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [expandedOrder, setExpandedOrder] = useState<number | null>(null);
  const [pdfType, setPdfType] = useState<'bdc' | 'bdl' | 'invoice' | null>(null);
  const [loadingPdf, setLoadingPdf] = useState<boolean>(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  useEffect(() => {
    void load();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => void load(), 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = async (): Promise<void> => {
    setBusy(true);
    try {
      // Charger le profil pour vérifier le statut
      const profileData = await getMyProfile();
      setProfile(profileData);

      const params: any = {};
      if (search) params.search = search;
      const data = await listMyOrders(params);
      setOrders(data);
    } catch (e) {
      console.error('Load orders error:', e);
    } finally {
      setBusy(false);
    }
  };

  const handleViewPdf = async (orderId: number, type: 'bdc' | 'bdl' | 'invoice'): Promise<void> => {
    if (expandedOrder === orderId && pdfType === type) {
      setExpandedOrder(null);
      setPdfType(null);
      setPdfUrl(null);
      return;
    }

    setLoadingPdf(true);
    setExpandedOrder(orderId);
    setPdfType(type);
    setPdfUrl(null);

    const token = useAuthStore.getState().token;
    if (!token) { setLoadingPdf(false); return; }

    try {
      if (type === 'bdc') {
        setPdfUrl(`${API_BASE_URL}/clients/me/orders/${orderId}/bdc/pdf?token=${encodeURIComponent(token)}`);
      } else if (type === 'bdl') {
        setPdfUrl(`${API_BASE_URL}/clients/me/orders/${orderId}/delivery-note/pdf?token=${encodeURIComponent(token)}`);
      } else if (type === 'invoice') {
        const invoiceResponse = await api.get(`/clients/me/orders/${orderId}/invoice`);
        if (invoiceResponse.data?.id) {
          setPdfUrl(`${API_BASE_URL}/clients/me/invoices/${invoiceResponse.data.id}/pdf?token=${encodeURIComponent(token)}`);
        }
      }
    } catch (error) {
      console.error('Erreur chargement PDF:', error);
      setExpandedOrder(null);
      setPdfType(null);
    } finally {
      setLoadingPdf(false);
    }
  };

  const closePdfViewer = (): void => {
    setExpandedOrder(null);
    setPdfType(null);
    setPdfUrl(null);
  };

  const handleNewOrder = () => {
    console.log('🚀 handleNewOrder clicked');
    console.log('👤 User status:', user?.status);
    console.log('📄 Profile status:', profile?.status);

    // Priority check on user status from context, then profile status
    const isBlocked = user?.status === 'bloque' || (profile && profile.status === 'bloque');
    console.log('🚫 isBlocked:', isBlocked);

    if (isBlocked) {
      console.log('显示 blocked modal');
      setShowBlockedModal(true);
      return;
    }
    console.log('➡️ Navigating to new order');
    navigate('/client/orders/new');
  };

  const stats = {
    total: orders.length,
    pending: orders.filter(o => o.status === 'pending').length,
    confirmed: orders.filter(o => o.status === 'confirmed').length,
    shipped: orders.filter(o => o.status === 'shipped').length,
    delivered: orders.filter(o => o.status === 'delivered').length,
    invoiced: orders.filter(o => o.status === 'invoiced').length,
    paid: orders.filter(o => o.status === 'paid').length,
  };

  const formatAmountHT = (amount?: number | null) => {
    return `${Math.round(amount || 0).toLocaleString('fr-FR')} € HT`;
  };

  const getCurrentTotalHT = () => {
    const targetOrders = statusFilter
      ? orders.filter(o => o.status === statusFilter)
      : orders.filter(o => o.status !== 'cancelled');
    return Math.round(targetOrders.reduce((sum, o) => sum + ((o.subtotal || o.total_amount) - (o.credit_note_amount || 0)), 0));
  };

  const filteredOrders = orders.filter(order => {
    if (statusFilter && order.status !== statusFilter) return false;
    return true;
  });

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-slate-50 via-orange-50/20 to-slate-50 dark:from-slate-950 dark:via-orange-950/10 dark:to-slate-950">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between px-4 sm:px-6 py-4 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-b border-slate-200/50 dark:border-slate-700/50 shadow-lg">
        <div className="flex items-center gap-3 sm:gap-4">
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-cyan-500 via-blue-500 to-cyan-600 rounded-xl flex items-center justify-center shadow-lg">
            <ShoppingCart className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-100">Mes Commandes</h1>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleNewOrder}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 text-white rounded-lg font-medium shadow-lg hover:shadow-xl transition-all duration-200"
          >
            <ShoppingCart className="h-5 w-5" />
            Créer une commande
          </button>
        </div>
      </div>

      {/* Stats Summary Bar */}
      {/* Mobile: compact 4-col grid with icon+count only (< md) */}
      <div className="md:hidden px-3 py-2">
        <div className="grid grid-cols-4 gap-1.5">
          <button onClick={() => setStatusFilter('')} className={`flex flex-col items-center gap-0.5 rounded-lg border py-1.5 transition-all ${!statusFilter ? 'border-blue-400 bg-blue-50 dark:bg-blue-950/30 shadow-sm' : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900'}`}>
            <ShoppingCart className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
            <span className="text-xs font-bold text-slate-800 dark:text-slate-100">{stats.total}</span>
            <span className="text-[8px] uppercase font-semibold text-slate-400">Total</span>
          </button>
          <button onClick={() => setStatusFilter('pending')} className={`flex flex-col items-center gap-0.5 rounded-lg border py-1.5 transition-all ${statusFilter === 'pending' ? 'border-yellow-400 bg-yellow-50 dark:bg-yellow-950/30 shadow-sm' : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900'}`}>
            <Clock className="h-3.5 w-3.5 text-yellow-600 dark:text-yellow-400" />
            <span className="text-xs font-bold text-yellow-600 dark:text-yellow-400">{stats.pending}</span>
            <span className="text-[8px] uppercase font-semibold text-slate-400">Attente</span>
          </button>
          <button onClick={() => setStatusFilter('confirmed')} className={`flex flex-col items-center gap-0.5 rounded-lg border py-1.5 transition-all ${statusFilter === 'confirmed' ? 'border-indigo-400 bg-indigo-50 dark:bg-indigo-950/30 shadow-sm' : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900'}`}>
            <CheckCircle2 className="h-3.5 w-3.5 text-indigo-600 dark:text-indigo-400" />
            <span className="text-xs font-bold text-indigo-600 dark:text-indigo-400">{stats.confirmed}</span>
            <span className="text-[8px] uppercase font-semibold text-slate-400">Confirmé</span>
          </button>
          <button onClick={() => setStatusFilter('shipped')} className={`flex flex-col items-center gap-0.5 rounded-lg border py-1.5 transition-all ${statusFilter === 'shipped' ? 'border-purple-400 bg-purple-50 dark:bg-purple-950/30 shadow-sm' : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900'}`}>
            <Package className="h-3.5 w-3.5 text-purple-600 dark:text-purple-400" />
            <span className="text-xs font-bold text-purple-600 dark:text-purple-400">{stats.shipped}</span>
            <span className="text-[8px] uppercase font-semibold text-slate-400">Expédié</span>
          </button>
          <button onClick={() => setStatusFilter('delivered')} className={`flex flex-col items-center gap-0.5 rounded-lg border py-1.5 transition-all ${statusFilter === 'delivered' ? 'border-green-400 bg-green-50 dark:bg-green-950/30 shadow-sm' : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900'}`}>
            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
            <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400">{stats.delivered}</span>
            <span className="text-[8px] uppercase font-semibold text-slate-400">Livré</span>
          </button>
          <button onClick={() => setStatusFilter('invoiced')} className={`flex flex-col items-center gap-0.5 rounded-lg border py-1.5 transition-all ${statusFilter === 'invoiced' ? 'border-orange-400 bg-orange-50 dark:bg-orange-950/30 shadow-sm' : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900'}`}>
            <FileText className="h-3.5 w-3.5 text-orange-600 dark:text-orange-400" />
            <span className="text-xs font-bold text-orange-600 dark:text-orange-400">{stats.invoiced}</span>
            <span className="text-[8px] uppercase font-semibold text-slate-400">Facturé</span>
          </button>
          <button onClick={() => setStatusFilter('paid')} className={`flex flex-col items-center gap-0.5 rounded-lg border py-1.5 transition-all ${statusFilter === 'paid' ? 'border-teal-400 bg-teal-50 dark:bg-teal-950/30 shadow-sm' : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900'}`}>
            <Euro className="h-3.5 w-3.5 text-teal-600 dark:text-teal-400" />
            <span className="text-xs font-bold text-teal-600 dark:text-teal-400">{stats.paid}</span>
            <span className="text-[8px] uppercase font-semibold text-slate-400">Payé</span>
          </button>
          <div className="flex flex-col items-center justify-center rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 py-1.5">
            <span className="text-[10px] font-bold text-slate-800 dark:text-slate-200">{getCurrentTotalHT().toLocaleString('fr-FR')} €</span>
            <span className="text-[8px] uppercase font-semibold text-slate-400">HT</span>
          </div>
        </div>
      </div>

      {/* Desktop: original grid (md+) */}
      <div className="hidden md:block px-6 py-2">
        <div className="grid grid-cols-4 xl:grid-cols-8 gap-2">
          <button
            onClick={() => setStatusFilter('')}
            className={`group relative overflow-hidden rounded-xl border ${!statusFilter
              ? 'border-blue-400 shadow-md bg-white/90 dark:bg-slate-900/90'
              : 'border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 hover:border-blue-200/80'} px-2 py-1.5 text-left transition-all duration-200`}
          >
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-gradient-to-r from-blue-50/70 to-indigo-50/70 dark:from-blue-950/20 dark:to-indigo-950/20 pointer-events-none transition-opacity duration-200" />
            <div className="relative flex items-center justify-between h-full">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-blue-50 text-blue-600 dark:bg-blue-950/40 dark:text-blue-300 flex-shrink-0">
                  <ShoppingCart className="h-3.5 w-3.5" />
                </div>
                <div className="flex flex-col"><span className="text-[10px] font-bold uppercase text-slate-500 dark:text-slate-400">Total</span></div>
              </div>
              <div className="text-lg font-bold text-slate-800 dark:text-slate-100">{stats.total}</div>
            </div>
          </button>

          <button
            onClick={() => setStatusFilter('pending')}
            className={`group relative overflow-hidden rounded-xl border ${statusFilter === 'pending'
              ? 'border-yellow-400 shadow-md bg-white/90 dark:bg-slate-900/90'
              : 'border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 hover:border-yellow-200/80'} px-2 py-1.5 text-left transition-all duration-200`}
          >
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-gradient-to-r from-yellow-50/70 to-amber-50/70 dark:from-yellow-950/20 dark:to-amber-950/20 pointer-events-none transition-opacity duration-200" />
            <div className="relative flex items-center justify-between h-full">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-yellow-50 text-yellow-600 dark:bg-yellow-950/40 dark:text-yellow-300 flex-shrink-0">
                  <Clock className="h-3.5 w-3.5" />
                </div>
                <div className="flex flex-col"><span className="text-[10px] font-bold uppercase text-slate-500 dark:text-slate-400">Attente</span></div>
              </div>
              <div className="text-lg font-bold text-yellow-600 dark:text-yellow-400">{stats.pending}</div>
            </div>
          </button>

          <button
            onClick={() => setStatusFilter('confirmed')}
            className={`group relative overflow-hidden rounded-xl border ${statusFilter === 'confirmed'
              ? 'border-indigo-400 shadow-md bg-white/90 dark:bg-slate-900/90'
              : 'border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 hover:border-indigo-200/80'} px-2 py-1.5 text-left transition-all duration-200`}
          >
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-gradient-to-r from-indigo-50/70 to-blue-50/70 dark:from-indigo-950/20 dark:to-blue-950/20 pointer-events-none transition-opacity duration-200" />
            <div className="relative flex items-center justify-between h-full">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-indigo-50 text-indigo-600 dark:bg-indigo-950/40 dark:text-indigo-300 flex-shrink-0">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                </div>
                <div className="flex flex-col"><span className="text-[10px] font-bold uppercase text-slate-500 dark:text-slate-400">Confirmé</span></div>
              </div>
              <div className="text-lg font-bold text-indigo-600 dark:text-indigo-400">{stats.confirmed}</div>
            </div>
          </button>

          <button
            onClick={() => setStatusFilter('shipped')}
            className={`group relative overflow-hidden rounded-xl border ${statusFilter === 'shipped'
              ? 'border-purple-400 shadow-md bg-white/90 dark:bg-slate-900/90'
              : 'border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 hover:border-purple-200/80'} px-2 py-1.5 text-left transition-all duration-200`}
          >
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-gradient-to-r from-purple-50/70 to-fuchsia-50/70 dark:from-purple-950/20 dark:to-fuchsia-950/20 pointer-events-none transition-opacity duration-200" />
            <div className="relative flex items-center justify-between h-full">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-purple-50 text-purple-600 dark:bg-purple-950/40 dark:text-purple-300 flex-shrink-0">
                  <Package className="h-3.5 w-3.5" />
                </div>
                <div className="flex flex-col"><span className="text-[10px] font-bold uppercase text-slate-500 dark:text-slate-400">Expédié</span></div>
              </div>
              <div className="text-lg font-bold text-purple-600 dark:text-purple-400">{stats.shipped}</div>
            </div>
          </button>

          <button
            onClick={() => setStatusFilter('delivered')}
            className={`group relative overflow-hidden rounded-xl border ${statusFilter === 'delivered'
              ? 'border-green-400 shadow-md bg-white/90 dark:bg-slate-900/90'
              : 'border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 hover:border-green-200/80'} px-2 py-1.5 text-left transition-all duration-200`}
          >
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-gradient-to-r from-emerald-50/70 to-teal-50/70 dark:from-emerald-950/20 dark:to-teal-950/20 pointer-events-none transition-opacity duration-200" />
            <div className="relative flex items-center justify-between h-full">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-300 flex-shrink-0">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                </div>
                <div className="flex flex-col"><span className="text-[10px] font-bold uppercase text-slate-500 dark:text-slate-400">Livré</span></div>
              </div>
              <div className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{stats.delivered}</div>
            </div>
          </button>

          <button
            onClick={() => setStatusFilter('invoiced')}
            className={`group relative overflow-hidden rounded-xl border ${statusFilter === 'invoiced'
              ? 'border-orange-400 shadow-md bg-white/90 dark:bg-slate-900/90'
              : 'border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 hover:border-orange-200/80'} px-2 py-1.5 text-left transition-all duration-200`}
          >
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-gradient-to-r from-orange-50/70 to-amber-50/70 dark:from-orange-950/20 dark:to-amber-950/20 pointer-events-none transition-opacity duration-200" />
            <div className="relative flex items-center justify-between h-full">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-orange-50 text-orange-600 dark:bg-orange-950/40 dark:text-orange-300 flex-shrink-0">
                  <FileText className="h-3.5 w-3.5" />
                </div>
                <div className="flex flex-col"><span className="text-[10px] font-bold uppercase text-slate-500 dark:text-slate-400">Facturé</span></div>
              </div>
              <div className="text-lg font-bold text-orange-600 dark:text-orange-400">{stats.invoiced}</div>
            </div>
          </button>

          <button
            onClick={() => setStatusFilter('paid')}
            className={`group relative overflow-hidden rounded-xl border ${statusFilter === 'paid'
              ? 'border-teal-400 shadow-md bg-white/90 dark:bg-slate-900/90'
              : 'border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 hover:border-teal-200/80'} px-2 py-1.5 text-left transition-all duration-200`}
          >
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-gradient-to-r from-teal-50/70 to-emerald-50/70 dark:from-teal-950/20 dark:to-emerald-950/20 pointer-events-none transition-opacity duration-200" />
            <div className="relative flex items-center justify-between h-full">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-teal-50 text-teal-600 dark:bg-teal-950/40 dark:text-teal-300 flex-shrink-0">
                  <Euro className="h-3.5 w-3.5" />
                </div>
                <div className="flex flex-col"><span className="text-[10px] font-bold uppercase text-slate-500 dark:text-slate-400">Payé</span></div>
              </div>
              <div className="text-lg font-bold text-teal-600 dark:text-teal-400">{stats.paid}</div>
            </div>
          </button>

          <div className="relative overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 px-2 py-1.5 text-left flex flex-col justify-center">
            <div className="absolute inset-0 bg-gradient-to-br from-slate-50 to-white dark:from-slate-900 dark:to-slate-950 pointer-events-none" />
            <div className="relative w-full">
              <div className="text-[10px] font-semibold uppercase text-slate-500 dark:text-slate-400 truncate leading-none mb-1">
                Total {statusFilter ? (statusConfig[statusFilter as keyof typeof statusConfig]?.label || statusFilter) : 'Global'} HT
              </div>
              <div className="text-lg font-bold bg-gradient-to-r from-slate-900 to-slate-700 dark:from-slate-100 dark:to-slate-300 bg-clip-text text-transparent truncate leading-none">
                {getCurrentTotalHT().toLocaleString('fr-FR')} € HT
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="px-3 py-3 sm:px-6 flex flex-row gap-2 sm:gap-4 items-center justify-between">
        <div className="relative flex-1 w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher par numéro de commande..."
            className="w-full h-10 pl-10 pr-4 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
        </div>

        <div className="flex items-center gap-1 border border-slate-300 dark:border-slate-600 rounded-lg p-1 bg-white dark:bg-slate-800">
          <button
            onClick={() => setViewMode('card')}
            title="Vue cartes"
            aria-label="Vue cartes"
            className={`p-2 rounded ${viewMode === 'card' ? 'bg-orange-100 dark:bg-orange-950/30 text-orange-700' : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800'}`}
          >
            <Grid3x3 className="h-4 w-4" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            title="Vue liste"
            aria-label="Vue liste"
            className={`p-2 rounded ${viewMode === 'list' ? 'bg-orange-100 dark:bg-orange-950/30 text-orange-700' : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800'}`}
          >
            <ListIcon className="h-4 w-4" />
          </button>
          <button
            onClick={() => setViewMode('table')}
            title="Vue tableau"
            aria-label="Vue tableau"
            className={`hidden md:block p-2 rounded ${viewMode === 'table' ? 'bg-orange-100 dark:bg-orange-950/30 text-orange-700' : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800'}`}
          >
            <TableIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto px-6 pb-6">
        {busy ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-500 dark:text-slate-400">
            <ShoppingCart className="h-16 w-16 mb-4 opacity-50" />
            <p className="text-lg font-medium">Aucune commande trouvée</p>
          </div>
        ) : viewMode === 'card' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredOrders.map((order, idx) => {
              const status = statusConfig[order.status] || statusConfig.pending;
              const StatusIcon = status.icon;
              return (
                <div
                  key={order.id}
                  className="group relative p-5 rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:shadow-xl hover:border-orange-300 dark:hover:border-orange-700 hover:scale-[1.02] transition-all duration-300 ease-out animate-in fade-in slide-in-from-bottom-4 cursor-pointer"
                  style={{ animationDelay: `${idx * 50}ms`, animationFillMode: 'backwards' }}
                  onClick={() => navigate(`/client/orders/${order.id}`)}
                >
                  <div className="absolute top-3 right-3">
                    <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${status.color}`}>
                      <StatusIcon className="h-3 w-3" />
                      {status.label}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-orange-100 to-amber-100 dark:from-orange-900/30 dark:to-amber-900/30 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm">
                      <ShoppingCart className="h-6 w-6 text-orange-600 dark:text-orange-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-slate-900 dark:text-slate-100 truncate">{order.order_number}</h3>
                      <p className="text-sm text-slate-600 dark:text-slate-400 truncate">{order.order_number}</p>
                    </div>
                  </div>
                  <div className="space-y-3 mb-4">
                    <div className="flex items-center gap-3 text-sm">
                      <div className="w-7 h-7 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0">
                        <Calendar className="h-3.5 w-3.5 text-emerald-500 dark:text-emerald-400" />
                      </div>
                      <span className="text-slate-600 dark:text-slate-400 font-medium">{new Date(order.created_at).toLocaleDateString('fr-FR')}</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <div className="w-7 h-7 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0">
                        <Package className="h-3.5 w-3.5 text-emerald-500 dark:text-emerald-400" />
                      </div>
                      <span className="text-slate-600 dark:text-slate-400 font-medium">#{order.order_number.split('-').pop()}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm font-bold text-orange-600 pl-1">
                      {formatAmountHT((order.subtotal || order.total_amount) - (order.credit_note_amount || 0))}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        void handleViewPdf(order.id, 'bdc');
                      }}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg border border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-600 dark:bg-blue-950/30 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-950/50 transition-colors"
                    >
                      <FileText className="h-3 w-3" />
                      BDC
                    </button>

                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        void handleViewPdf(order.id, 'bdl');
                      }}
                      disabled={order.status === 'pending' || order.status === 'cancelled'}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${expandedOrder === order.id && pdfType === 'bdl'
                        ? 'bg-purple-600 text-white shadow-md border border-purple-600'
                        : 'border border-purple-300 bg-purple-50 text-purple-700 hover:bg-purple-100 dark:border-purple-600 dark:bg-purple-950/30 dark:text-purple-300 dark:hover:bg-purple-950/50'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      <Truck className="h-3.5 w-3.5" />
                      BDL
                    </button>

                    {(order.status === 'invoiced' || order.status === 'paid') && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          void handleViewPdf(order.id, 'invoice');
                        }}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${expandedOrder === order.id && pdfType === 'invoice'
                          ? 'bg-green-600 text-white shadow-md border border-green-600'
                          : 'border border-green-300 bg-green-50 text-green-700 hover:bg-green-100 dark:border-green-600 dark:bg-green-950/30 dark:text-green-300 dark:hover:bg-green-950/50'
                          }`}
                      >
                        <Euro className="h-3.5 w-3.5" />
                        Facture
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : viewMode === 'list' ? (
          <div className="space-y-3">
            {filteredOrders.map((order, idx) => {
              const status = statusConfig[order.status] || statusConfig.pending;
              const StatusIcon = status.icon;
              return (
                <div
                  key={order.id}
                  className="group p-4 sm:p-5 rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:shadow-lg hover:border-orange-300 dark:hover:border-orange-700 hover:translate-x-1 transition-all duration-300 ease-out animate-in fade-in slide-in-from-left-4 cursor-pointer"
                  style={{ animationDelay: `${idx * 30}ms`, animationFillMode: 'backwards' }}
                  onClick={() => navigate(`/client/orders/${order.id}`)}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4">
                    <div className="flex items-center gap-4 min-w-0 flex-1">
                      <div className="w-12 h-12 bg-gradient-to-br from-orange-100 to-amber-100 dark:from-orange-900/30 dark:to-amber-900/30 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm">
                        <ShoppingCart className="h-6 w-6 text-orange-600 dark:text-orange-400" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-3 mb-1">
                          <h3 className="font-bold text-lg text-slate-900 dark:text-slate-100">{order.order_number}</h3>
                          <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium shadow-sm ${status.color}`}>
                            <StatusIcon className="h-3.5 w-3.5" />
                            {status.label}
                          </div>
                        </div>
                        <p className="text-sm text-slate-500 dark:text-slate-400 truncate">
                          Réf: {order.order_number}
                          {order.notes && <span className="mx-2">•</span>}
                          {order.notes}
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-6 text-sm sm:border-l sm:border-slate-200 sm:dark:border-slate-700 sm:pl-6">
                      <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                        <Calendar className="h-4 w-4 text-emerald-500 dark:text-emerald-400" />
                        <span>{new Date(order.created_at).toLocaleDateString('fr-FR')}</span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                        <Package className="h-4 w-4 text-emerald-500 dark:text-emerald-400" />
                        {/* Note: Item count not available in list summary, showing placeholder or need to fetch details */}
                        <span>Commande</span>
                      </div>
                      <div className="flex items-center gap-2 font-bold text-lg text-orange-600 bg-orange-50 dark:bg-orange-950/30 px-3 py-1 rounded-lg">
                        {formatAmountHT((order.subtotal || order.total_amount) - (order.credit_note_amount || 0))}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 pt-4 border-t border-slate-100 dark:border-slate-800">
                    {/* Bouton BDC avec visualiseur */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        void handleViewPdf(order.id, 'bdc');
                      }}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${expandedOrder === order.id && pdfType === 'bdc'
                        ? 'bg-blue-600 text-white shadow-md'
                        : 'bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-950/30 dark:text-blue-300 dark:hover:bg-blue-950/50 border border-blue-200 dark:border-blue-800'
                        }`}
                    >
                      <FileText className="h-3.5 w-3.5" />
                      BDC
                      {expandedOrder === order.id && pdfType === 'bdc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                    </button>

                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        void handleViewPdf(order.id, 'bdl');
                      }}
                      disabled={order.status === 'pending' || order.status === 'cancelled'}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${expandedOrder === order.id && pdfType === 'bdl'
                        ? 'bg-purple-600 text-white shadow-md border border-purple-600'
                        : 'border border-purple-300 bg-purple-50 text-purple-700 hover:bg-purple-100 dark:border-purple-600 dark:bg-purple-950/30 dark:text-purple-300 dark:hover:bg-purple-950/50'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      <Truck className="h-3.5 w-3.5" />
                      BDL
                    </button>

                    {/* Bouton Facture avec visualiseur */}
                    {(order.status === 'invoiced' || order.status === 'paid') && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          void handleViewPdf(order.id, 'invoice');
                        }}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${expandedOrder === order.id && pdfType === 'invoice'
                          ? 'bg-green-600 text-white shadow-md border border-green-600'
                          : 'border border-green-300 bg-green-50 text-green-700 hover:bg-green-100 dark:border-green-600 dark:bg-green-950/30 dark:text-green-300 dark:hover:bg-green-950/50'
                          }`}
                      >
                        <Euro className="h-3.5 w-3.5" />
                        Facture
                        {expandedOrder === order.id && pdfType === 'invoice' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                      </button>
                    )}

                  </div>
                </div>
              );
            })}
          </div>
        ) : viewMode === 'table' ? (
          <div className="bg-white dark:bg-slate-900 rounded-xl border-2 border-slate-200 dark:border-slate-700 overflow-hidden shadow-lg">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-950/30 dark:to-amber-950/30 border-b-2 border-orange-200 dark:border-orange-800">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">N° Commande</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Référence</th>
                    <th className="px-4 py-3 text-center text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Statut</th>
                    <th className="px-4 py-3 text-right text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Montant</th>
                    <th className="px-4 py-3 text-center text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {filteredOrders.map((order, idx) => {
                    const status = statusConfig[order.status] || statusConfig.pending;
                    const StatusIcon = status.icon;
                    return (
                      <tr
                        key={order.id}
                        className="hover:bg-orange-50/50 dark:hover:bg-orange-950/20 transition-colors duration-200 animate-in fade-in slide-in-from-bottom-2 cursor-pointer"
                        style={{ animationDelay: `${idx * 20}ms`, animationFillMode: 'backwards' }}
                        onClick={() => navigate(`/client/orders/${order.id}`)}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-orange-100 to-amber-100 dark:from-orange-900/30 dark:to-amber-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                              <ShoppingCart className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                            </div>
                            <span className="font-semibold text-slate-900 dark:text-slate-100">{order.order_number}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
                          {new Date(order.created_at).toLocaleDateString('fr-FR')}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
                          {order.order_number}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-center">
                            <div className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${status.color}`}>
                              <StatusIcon className="h-3 w-3" />
                              {status.label}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right font-bold text-orange-600">
                          {formatAmountHT((order.subtotal || order.total_amount) - (order.credit_note_amount || 0))}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-center gap-1">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                void handleViewPdf(order.id, 'bdc');
                              }}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-lg border border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-600 dark:bg-blue-950/30 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-950/50 transition-colors"
                              title="Afficher BDC"
                            >
                              <FileText className="h-3 w-3" />
                              BDC
                            </button>

                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                void handleViewPdf(order.id, 'bdl');
                              }}
                              disabled={order.status === 'pending' || order.status === 'cancelled'}
                              className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-lg transition-all ${expandedOrder === order.id && pdfType === 'bdl'
                                ? 'bg-purple-600 text-white shadow-md border border-purple-600'
                                : 'border border-purple-300 bg-purple-50 text-purple-700 hover:bg-purple-100 dark:border-purple-600 dark:bg-purple-950/30 dark:text-purple-300 dark:hover:bg-purple-950/50'
                                } disabled:opacity-50 disabled:cursor-not-allowed`}
                              title="Afficher BDL"
                            >
                              <Truck className="h-3 w-3" />
                              BDL
                            </button>

                            {(order.status === 'invoiced' || order.status === 'paid') && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  void handleViewPdf(order.id, 'invoice');
                                }}
                                className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-lg transition-all ${expandedOrder === order.id && pdfType === 'invoice'
                                  ? 'bg-green-600 text-white shadow-md border border-green-600'
                                  : 'border border-green-300 bg-green-50 text-green-700 hover:bg-green-100 dark:border-green-600 dark:bg-green-950/30 dark:text-green-300 dark:hover:bg-green-950/50'
                                  }`}
                                title="Afficher facture"
                              >
                                <Euro className="h-3 w-3" />
                                Facture
                              </button>
                            )}

                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </div>

      {showBlockedModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4 animate-in fade-in duration-200">
          <div className="max-w-md w-full bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border-2 border-red-100 dark:border-red-900/30 p-8 text-center animate-in zoom-in duration-300">
            <div className="w-20 h-20 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <ShieldAlert className="h-10 w-10 text-red-600 dark:text-red-400" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-4">Compte Bloqué</h2>
            <p className="text-slate-600 dark:text-slate-400 mb-8">
              Votre compte est temporairement bloqué. Merci de contacter l'administrateur pour passer commande.
            </p>
            <button
              onClick={() => setShowBlockedModal(false)}
              className="w-full py-3 bg-slate-900 dark:bg-slate-800 text-white rounded-xl font-bold hover:bg-slate-800 dark:hover:bg-slate-700 transition-colors"
            >
              Compris
            </button>
          </div>
        </div>
      )}

      {/* Modal PDF Viewer */}
      {expandedOrder !== null && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[90] p-0 sm:p-4" onClick={closePdfViewer}>
          <div className="bg-white dark:bg-slate-900 sm:rounded-2xl shadow-2xl w-full h-full sm:max-w-5xl sm:h-[85vh] border-0 sm:border border-slate-200 dark:border-slate-800 animate-in zoom-in-95 fade-in duration-200 flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
              <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                {pdfType === 'bdc' && '📄 Bon de Commande'}
                {pdfType === 'bdl' && '🚚 Bon de Livraison'}
                {pdfType === 'invoice' && '🧾 Facture'}
              </h4>
              <div className="flex items-center gap-2">
                <a
                  href={pdfUrl || '#'}
                  download
                  className="p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                  title="Télécharger"
                  aria-label="Télécharger le PDF"
                >
                  <Download className="h-4 w-4" />
                </a>
                <button onClick={closePdfViewer} className="p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="flex-1 p-4">
              {loadingPdf ? (
                <div className="flex items-center justify-center h-full bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-3"></div>
                    <p className="text-sm text-slate-600 dark:text-slate-400">Chargement du PDF...</p>
                  </div>
                </div>
              ) : pdfUrl ? (
                <iframe src={pdfUrl} className="w-full h-full rounded-lg border-2 border-slate-200 dark:border-slate-700 bg-white" title={`PDF ${pdfType}`} />
              ) : (
                <div className="flex items-center justify-center h-full bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <p className="text-sm text-slate-600 dark:text-slate-400">Impossible de charger le PDF</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BuyerOrdersTailwind;
